import { TalentNode } from '../data/unifiedTalentTree';

// Health impact values for each talent (percentage of age-based maximum)
// ONLY LONGEVITY TALENTS GIVE YEARS BACK
// Maximum years requires completing ALL longevity talents
const TALENT_HEALTH_IMPACT: Record<string, number> = {
  // Major longevity talents (60% of total effect combined)
  'zone-two-cardio': 0.25,     // 25% of maximum years
  'strength-training': 0.25,   // 25% of maximum years  
  'zone-five-cardio': 0.10,    // 10% of maximum years
  // Major talents subtotal: 60%
  
  // Important longevity talents (remaining 40%)
  'sleep-hygiene': 0.10,       // 10% of maximum years
  'oral-health': 0.06,         // 6% of maximum years
  'energy-balance': 0.10,      // 10% of maximum years
  'time-restricted-eating': 0.06, // 6% of maximum years
  'fiber-intake': 0.04,        // 4% of maximum years
  'protein-optimization': 0.03, // 3% of maximum years
  'creatine-supplementation': 0.01, // 1% of maximum years
  // Remaining talents subtotal: 40%
  // Total: 100% when all longevity talents are completed
  
  // Healthspan talents - NO YEARS GAINED (0 impact)
  'stability-mastery': 0.0,
  'functional-mobility': 0.0,
  'elastic-edge': 0.0,
  'recovery-mastery': 0.0,
  
  // Wellness talents - NO YEARS GAINED (0 impact)
  'sunseeker': 0.0,
  'guided-breathwork': 0.0,
  'social-connection': 0.0,
  'purpose-meaning': 0.0,
  'emotional-regulation': 0.0,
  
  // Root talent
  'health-tree': 0.0,
};

// Health worth multiplier (impact on earning capacity)
const HEALTH_WORTH_MULTIPLIER = 12000; // Average additional annual earnings per year of life extension

export interface HealthStats {
  totalYearsGained: number;
  healthWorthIncrease: number;
  longevityYears: number;
  healthspanYears: number;
  wellnessYears: number;
  overallHealthScore: number;
  totalCostYears: number; // Total lifetime years invested in health activities
}

class HealthImpactService {
  private static instance: HealthImpactService;

  static getInstance(): HealthImpactService {
    if (!HealthImpactService.instance) {
      HealthImpactService.instance = new HealthImpactService();
    }
    return HealthImpactService.instance;
  }

  // Calculate health impact for a single talent (returns actual years gained)
  calculateTalentImpact(talentId: string, sessions: number, maxSessions: number, userAge?: number | null): number {
    const talentMaxPercentage = TALENT_HEALTH_IMPACT[talentId] || 0;
    const ageBasedMaxYears = this.getMaxYearsByAge(userAge);
    
    // Calculate the maximum years this talent can contribute
    const talentMaxYears = talentMaxPercentage * ageBasedMaxYears;
    
    if (sessions === 0) return 0;
    if (sessions >= maxSessions) return talentMaxYears;
    
    // Calculate what fraction of the talent's potential the user has achieved
    // Use diminishing returns curve for all talents except Zone 5 Cardio
    let completionFraction = 0;
    
    if (talentId === 'zone-five-cardio') {
      // Zone 5 Cardio: Linear progression (no diminishing returns)
      completionFraction = sessions / maxSessions;
    } else {
      // All other talents: Use diminishing returns curve
      let totalProgress = 0;
      let maxPossibleProgress = 0;
      
      // Calculate actual progress with diminishing returns
      for (let i = 1; i <= sessions; i++) {
        totalProgress += Math.pow(0.9, i - 1);
      }
      
      // Calculate max possible progress if all sessions completed
      for (let i = 1; i <= maxSessions; i++) {
        maxPossibleProgress += Math.pow(0.9, i - 1);
      }
      
      completionFraction = totalProgress / maxPossibleProgress;
    }
    
    // Return the fraction of this talent's maximum years
    return completionFraction * talentMaxYears;
  }

  // Get maximum years based on user age
  private getMaxYearsByAge(userAge?: number | null): number {
    if (!userAge) return 12; // Default maximum if no age provided
    
    // Age-based maximums as specified
    if (userAge <= 30) return 12;  // Ages 18-30: 12 years max
    if (userAge <= 40) return 10;  // Ages 31-40: 10 years max  
    if (userAge <= 50) return 8;   // Ages 41-50: 8 years max
    if (userAge <= 60) return 6;   // Ages 51-60: 6 years max
    if (userAge <= 70) return 4;   // Ages 61-70: 4 years max
    return 2;                      // Ages 71+: 2 years max
  }

  // Calculate overall health statistics based on all talents
  calculateHealthStats(talents: TalentNode[], userAge?: number | null): HealthStats {
    let rawYearsGained = 0;
    let longevityYears = 0;
    let healthspanYears = 0;
    let wellnessYears = 0;
    let totalWeeklyMinutes = 0;

    talents.forEach(talent => {
      if (talent.currentSession > 0) {
        const impact = this.calculateTalentImpact(talent.id, talent.currentSession, talent.maxSession, userAge);
        
        // Add up weekly time commitment for all talents
        totalWeeklyMinutes += talent.timeRequired * talent.currentSession;

        // Only longevity talents contribute years (others contribute 0)
        if (talent.category === 'longevity') {
          rawYearsGained += impact;
          longevityYears += impact;
        }
        // Healthspan and wellness talents contribute 0 years but still tracked for categories
        else if (talent.category === 'healthspan') {
          healthspanYears += 0; // Always 0 for healthspan
        }
        else if (talent.category === 'wellness') {
          wellnessYears += 0; // Always 0 for wellness
        }
      }
    });

    // Cap the total years to the age-specific maximum
    const maxYears = this.getMaxYearsByAge(userAge);
    const totalYearsGained = Math.min(rawYearsGained, maxYears);

    // Calculate total cost in lifetime years
    // Formula: (totalWeeklyMinutes / 60 minutes per hour) / (24 hours * 365 days) = years per year
    // Then multiply by years until 89: (89 - userAge)
    const yearsUntil89 = userAge ? Math.max(0, 89 - userAge) : 50; // Default to 50 years if no age
    const hoursPerWeek = totalWeeklyMinutes / 60;
    const hoursPerYear = hoursPerWeek * 52;
    const totalLifetimeHours = hoursPerYear * yearsUntil89;
    const totalCostYears = Math.round((totalLifetimeHours / (24 * 365)) * 100) / 100; // Convert to years, round to 2 decimals

    // Calculate health worth increase (lifetime earning impact)
    const workingYearsRemaining = userAge ? Math.max(0, 67 - userAge) : 40; // Use retirement age 67
    const healthWorthIncrease = totalYearsGained * HEALTH_WORTH_MULTIPLIER * workingYearsRemaining;

    // Calculate overall health score (0-100 scale) based on age-specific maximum
    const overallHealthScore = Math.min(100, (totalYearsGained / maxYears) * 100);

    return {
      totalYearsGained: Math.round(totalYearsGained * 10) / 10, // Round to 1 decimal
      healthWorthIncrease: Math.round(healthWorthIncrease),
      longevityYears: Math.round(longevityYears * 10) / 10,
      healthspanYears: 0, // Always 0 - healthspan talents don't give years
      wellnessYears: 0,   // Always 0 - wellness talents don't give years
      overallHealthScore: Math.round(overallHealthScore),
      totalCostYears
    };
  }

  // Get health impact for a specific talent (for tooltips)
  getTalentHealthImpact(talentId: string, currentSessions: number, maxSessions: number, userAge?: number | null): string {
    const currentImpact = this.calculateTalentImpact(talentId, currentSessions, maxSessions, userAge);
    const maxImpact = this.calculateTalentImpact(talentId, maxSessions, maxSessions, userAge);
    
    // Only show impact for longevity talents
    if (TALENT_HEALTH_IMPACT[talentId] === 0) {
      return `Quality of life improvement (no years gained)`;
    }
    
    if (currentSessions === 0) {
      return `Potential: +${maxImpact.toFixed(1)} years`;
    } else if (currentSessions === maxSessions) {
      return `Completed: +${currentImpact.toFixed(1)} years`;
    } else {
      return `Current: +${currentImpact.toFixed(1)} years (max: +${maxImpact.toFixed(1)})`;
    }
  }
}

export const healthImpactService = HealthImpactService.getInstance();
export default healthImpactService;